import PaytoIcon from 'assets/icons/payments/payto-icon-2.svg';
import CloseIcon from 'assets/images/icon_close.svg';
import ButtonCommon from 'components/common/ButtonCommon';
import SizeBox from 'components/common/SizeBox';
import TooltipCommon from 'components/common/TooltipCommon';
import { checkFormInputValidation } from 'components/common/func';
import {
  DESKTOP_MIN_WIDTH,
  MOBILE_MIN_WIDTH,
  TABLET_MAX_WIDTH,
} from 'constants';
import withAddPaytoAccountManual from 'features/to/HOCs/payment/withAddPaytoAccountManual';
import withAddPaytoAccountsAgreement from 'features/to/HOCs/payment/withAddPaytoAccountsAgreement';
import withAddPaytoBankVerified from 'features/to/HOCs/payment/withAddPaytoBankVerified';
import withAddPaytoPayIdAccountManual from 'features/to/HOCs/payment/withAddPaytoPayIdAccountManual';
import withCheckMaxAmount from 'features/to/HOCs/payment/withCheckMaxAmount';
import withRoundUpPaytoMaxAmount from 'features/to/HOCs/payment/withRoundUpPaytoMaxAmount';
import AccountNumberVerifiedItem from 'features/to/components/payment/payto/AccountNumberVerifiedItem';
import CustomMaxAmountDropdown from 'features/to/components/payment/payto/CustomMaxAmountDropdown';
import FormInputAmountValidation from 'features/to/components/payment/payto/FormInputAmountValidation';
import FormPayidAccountDetails from 'features/to/components/payment/payto/FormPayidAccountDetails';
import FormPaytoAccountDetails from 'features/to/components/payment/payto/FormPaytoAccountDetails';
import PaytoSystemErrorOverlay from 'features/to/components/payment/payto/PaytoSystemErrorOverlay';
import {
  FORM_ADD_PAYTO_ACCOUNT_NUMBER_FIELD,
  FORM_ADD_PAYTO_ACCOUNT_NUMBER_NAME,
  FORM_ADD_PAYTO_ACCOUNT_VALIDATION_INITIAL,
  FORM_ADD_PAYTO_BANK_NAME_NAME,
  FORM_ADD_PAYTO_BSB_FIELD,
  FORM_ADD_PAYTO_BSB_NAME,
  FORM_ADD_PAYTO_LIMIT_PER_AGREEMENT_FIELD,
  FORM_ADD_PAYTO_MAX_AMOUNT_FIELD,
  FORM_ADD_PAYTO_MAX_AMOUNT_NAME,
  FORM_ADD_PAYTO_PAYID_MOBILE_EMAIL_FIELD_REFERENCES,
  FORM_ADD_PAYTO_PAYID_MOBILE_EMAIL_NAME,
  FORM_ADD_PAYTO_PAYID_MOBILE_FIELD,
  FORM_ADD_PAYTO_PAYID_TYPE_NAME,
  MAX_AMOUNT_OPTION_DATA,
  PAYID_TYPE_MOBILE,
  PAYTO_LOG_TAG,
  isPayToLimitNullEquivalent,
} from 'features/to/const';
import usePaytoStore from 'features/to/hooks/payment/usePaytoStore';
import useFormInput from 'hooks/form/useFormInput';
import { useDetectDevice } from 'hooks/useDetectDevice';
import useLang from 'hooks/useLang';
import useTheme from 'hooks/useTheme';
import { isArray, isEmpty } from 'lodash';
import { Fragment, useCallback, useEffect, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import styled from 'styled-components';
import { stringUtils } from 'utils';
import {
  cursorPointerMixin,
  displayFlexColCenterCenterMixin,
  fontSize12MixinObject,
  fontSize14MixinObject,
  fontSize16MixinObject,
  paragraphMixin,
  paragraphMixinObject,
} from 'styles';

const FormAddPaytoAccountManual = withAddPaytoAccountManual(
  FormPaytoAccountDetails,
);
const FormAddPaytoPayIdAccountManual = withAddPaytoPayIdAccountManual(
  FormPayidAccountDetails,
);

const OtherAccountNumberVerifiedItem = withAddPaytoAccountManual(
  AccountNumberVerifiedItem,
);
const PayIdAccountNumberVerifiedItem = withAddPaytoPayIdAccountManual(
  AccountNumberVerifiedItem,
);
const BankAccountVerifiedItem = withAddPaytoBankVerified(
  AccountNumberVerifiedItem,
);

const FormInputMaxAmountValidationRoundUp = withRoundUpPaytoMaxAmount(
  FormInputAmountValidation,
);
const FormSelectionMaxAmountValidation = withCheckMaxAmount(
  CustomMaxAmountDropdown,
);

const Popup = withAddPaytoAccountsAgreement(
  ({
    isOpen,
    buttonLabel1,
    onClose,
    buttonLabel2,
    onClick,
    isBackdropOverlap,
    zIndexBackdropOverlap,
    zIndexModal = 0,
    isAddManual,
    setAddManual,
    isAddPayIdManual,
    setAddPayIdManual,
    onShowAuthorisePaytoAccountModal,
    onAddPaytoAccountAuthorise,
    onOpenPaytoErrorCodeModal,
    modalOptions,
    onSelect: onSelectPaytoAccountAgreement,
    isAddTypeSelected,
  }) => {
    const { t } = useLang();
    const { isDesktop } = useDetectDevice();
    const { isHHMTTheme } = useTheme();

    const { paytoAccountInformationRetry } = modalOptions || {};

    const [paytoAccountValidation, setPaytoAccountValidation] = useState(
      FORM_ADD_PAYTO_ACCOUNT_VALIDATION_INITIAL,
    );

    // DEV-1401: local, non-portal system-error overlay state -- see
    // PaytoSystemErrorOverlay.jsx for why this deliberately does not go
    // through the shared PortalModal. One shared instance (not per-field)
    // so two near-simultaneous transport failures update in place rather
    // than stacking a second overlay.
    const [paytoSystemError, setPaytoSystemError] = useState({
      isShow: false,
      title: '',
      content: '',
    });
    // DEV-1401: stable identities -- PaytoSystemErrorOverlay's own focus-
    // management effect depends on `onClose`, so an unstable reference here
    // would re-run that effect (and its focus-restore cleanup) on every
    // Popup render while the overlay is shown.
    const onPaytoSystemError = useCallback((title, content) => {
      setPaytoSystemError({ isShow: true, title, content });
    }, []);
    const onClosePaytoSystemError = useCallback(() => {
      setPaytoSystemError((prev) => ({ ...prev, isShow: false }));
    }, []);

    const {
      maxAmount,
      paytoAccountInformation,
      setPaytoAccountInformation,
      paytoBankAccountsVerified,
      resetPaytoAccountInformation,
      paytoAccountsAgreement,
      payIdType,
      paytoCreateLimit,
      paytoAmendLimit,
      paytoBsbNumberCheckPending,
      paytoBsbAccountCheckPending,
      paytoPayIdCheckPending,
      paytoPayIdBankValidationFailure,
    } = usePaytoStore();

    // DEV-1401: whether to show the separate PayID bank-validation-failure
    // row (and, correspondingly, suppress the field's own inline message
    // text) -- gated on isAddPayIdManual so it can never render before the
    // manual PayID form itself is even shown.
    const shouldShowPayIdBankValidationFailure =
      isAddPayIdManual && paytoPayIdBankValidationFailure;

    // DEV-1401 (corrected): amendPayToLimit for the Create flow is read from
    // the same live BSB/PayID validation response that supplies
    // createPayToLimit (validateBSBVer2 / validatePayId; also read
    // opportunistically from validateBsbAccount behind its own hasOwnProperty
    // guard, since that endpoint's support for the field is unconfirmed --
    // see the DEV-1401 ruling doc's client-confirmation list), stored in its
    // own paytoAmendLimit slot -- see withCheckBSBNumber.jsx /
    // withCheckBsbAccount.jsx / withCheckMobileNumber.jsx. NOT read from
    // paytoAccountInformation: that field is only ever populated when
    // re-selecting an existing agreement via withAddPaytoAccountsAgreement.jsx,
    // and is unconditionally wiped on every Create-modal mount by
    // withAddPaytoAccountInitial.jsx before any validation can run, so it can
    // never carry a real value for a freshly-entered BSB/PayID.
    const amendPayToLimit = paytoAmendLimit;

    // DEV-1401: hide the maxAmount UI only while the APPLICABLE
    // createPayToLimit-affecting validation is unresolved -- BSB has two
    // calls that can each supply/override the limit (the BSB-number check
    // and the BSB-account check); PayID has one. Never the global
    // isLoading flag (row #5).
    const isCreateLimitCheckPending = isAddManual
      ? paytoBsbNumberCheckPending || paytoBsbAccountCheckPending
      : isAddPayIdManual
        ? paytoPayIdCheckPending
        : false;

    // DEV-1401: label switches from the static default to label_max_amount
    // once a positive createPayToLimit has actually been resolved.
    const maxAmountFieldLabel = isPayToLimitNullEquivalent(paytoCreateLimit)
      ? 'label_add_payto_account_popup_2'
      : 'label_max_amount';

    // DEV-1401 (Corrected Create precedence): show the "can increase limit"
    // guidance row when createPayToLimit is below amendPayToLimit, or when
    // amendPayToLimit itself is not yet known/null-equivalent. If
    // createPayToLimit is ALSO null-equivalent (no cap resolved), there is
    // nothing to guide the user about, so guidance is suppressed in that
    // sub-case.
    const shouldShowLimitGuidance = isPayToLimitNullEquivalent(paytoCreateLimit)
      ? false
      : isPayToLimitNullEquivalent(amendPayToLimit) ||
        Number(paytoCreateLimit) < Number(amendPayToLimit);

    // DEV-1401: filter MAX_AMOUNT_OPTION_DATA (or an already-narrower list,
    // e.g. the legacy limit1000 allowlist) down to options the resolved
    // createPayToLimit actually permits. A null-equivalent limit means "no
    // cap" -- the full incoming list, not zero options. If filtering would
    // leave zero options (a createPayToLimit below the smallest static
    // option), no option in the incoming list actually satisfies the limit,
    // so fall back to the single smallest option (the incoming list is
    // ascending) rather than the full unfiltered list -- falling back to the
    // full list let the clamp-effect below pick the LARGEST option as its
    // "safe" fallback, silently pre-selecting the worst possible value.
    const getMaxAmountOptionsForCreateLimit = (options) => {
      if (isPayToLimitNullEquivalent(paytoCreateLimit)) return options;
      const filtered = options.filter(
        (option) => Number(option.value) <= Number(paytoCreateLimit),
      );
      return isEmpty(filtered) ? [options[0]] : filtered;
    };

    // DEV-1401: clamp/reset the selected maxAmount value whenever the
    // resolved createPayToLimit changes so a previously-selected value
    // that now exceeds the limit (or that isn't the sole remaining
    // option) cannot linger as the still-selected value.
    useEffect(() => {
      const filteredOptions = getMaxAmountOptionsForCreateLimit(
        MAX_AMOUNT_OPTION_DATA,
      );
      const currentValue =
        paytoAccountInformation?.[FORM_ADD_PAYTO_MAX_AMOUNT_NAME];

      if (filteredOptions.length === 1) {
        if (`${currentValue}` !== `${filteredOptions[0].value}`) {
          setPaytoAccountInformation({
            ...paytoAccountInformation,
            [FORM_ADD_PAYTO_MAX_AMOUNT_NAME]: filteredOptions[0].value,
          });
        }
        return;
      }

      if (isPayToLimitNullEquivalent(paytoCreateLimit)) return;

      const numericCurrentValue = Number(currentValue);
      if (
        numericCurrentValue &&
        numericCurrentValue > Number(paytoCreateLimit)
      ) {
        const fallbackOption = filteredOptions[filteredOptions.length - 1];
        setPaytoAccountInformation({
          ...paytoAccountInformation,
          [FORM_ADD_PAYTO_MAX_AMOUNT_NAME]:
            fallbackOption?.value || MAX_AMOUNT_OPTION_DATA[0].value,
        });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paytoCreateLimit]);

    const handleOnSubmit = async () => {
      // DEV-1401: the underlying submit button's Enter-to-submit listener
      // (useEnterKeyDown, via ButtonCommon) stays mounted regardless of the
      // system-error overlay's own state -- it is gated only by isLoading/
      // isDesktop, never by isDisabled. Guard here so an Enter keypress
      // aimed at dismissing the overlay cannot also submit or mutate the
      // underlying form's validation state while it's shown.
      if (paytoSystemError.isShow) return;

      try {
        let newPaytoAccountInformation = { ...paytoAccountInformation };
        let newPaytoAccountValidation = { ...paytoAccountValidation };

        if (isAddPayIdManual) {
          delete newPaytoAccountInformation[FORM_ADD_PAYTO_BSB_NAME];
          delete newPaytoAccountValidation[FORM_ADD_PAYTO_BSB_NAME];
          delete newPaytoAccountInformation[FORM_ADD_PAYTO_BANK_NAME_NAME];
          delete newPaytoAccountValidation[FORM_ADD_PAYTO_BANK_NAME_NAME];
          delete newPaytoAccountInformation[FORM_ADD_PAYTO_ACCOUNT_NUMBER_NAME];
          delete newPaytoAccountValidation[FORM_ADD_PAYTO_ACCOUNT_NUMBER_NAME];
        }

        if (isAddManual) {
          if (isEmpty(paytoAccountInformation[FORM_ADD_PAYTO_BANK_NAME_NAME])) {
            delete newPaytoAccountInformation[FORM_ADD_PAYTO_BANK_NAME_NAME];
            delete newPaytoAccountValidation[FORM_ADD_PAYTO_BANK_NAME_NAME];
          }
          delete newPaytoAccountInformation[FORM_ADD_PAYTO_PAYID_TYPE_NAME];
          delete newPaytoAccountValidation[FORM_ADD_PAYTO_PAYID_TYPE_NAME];
          delete newPaytoAccountInformation[
            FORM_ADD_PAYTO_PAYID_MOBILE_EMAIL_NAME
          ];
          delete newPaytoAccountValidation[
            FORM_ADD_PAYTO_PAYID_MOBILE_EMAIL_NAME
          ];
        }

        console.log(
          `⭐ ${PAYTO_LOG_TAG} 👉 handleOnSubmit ➡️ newPaytoAccountInformation ↩`,
          newPaytoAccountInformation,
        );
        console.log(
          `⭐ ${PAYTO_LOG_TAG} 👉 handleOnSubmit ➡️ newPaytoAccountValidation ↩`,
          newPaytoAccountValidation,
        );

        // DEV-1401: defensive backstop -- normal clamp/reset (the
        // useEffect above) should already keep the selected value at or
        // below createPayToLimit, but the submitted value is enforced
        // here too rather than trusted blindly. Silent block, no new
        // message -- Create has no hard-stop/warning state under this
        // design (see the footer-state matrix).
        const isMaxAmountWithinCreateLimit =
          isPayToLimitNullEquivalent(paytoCreateLimit) ||
          Number(
            newPaytoAccountInformation?.[FORM_ADD_PAYTO_MAX_AMOUNT_NAME],
          ) <= Number(paytoCreateLimit);

        const isAllowSubmit =
          Object.values(newPaytoAccountInformation).every((value) => value) &&
          Object.values(newPaytoAccountValidation).every((value) => !value) &&
          isMaxAmountWithinCreateLimit;
        if (isAllowSubmit) {
          if (isAddManual) {
            console.log(
              `⭐ ${PAYTO_LOG_TAG} 👉 isAllowSubmit ➡️ isAddManual ➡️ paytoAccountInformation ↩`,
              paytoAccountInformation,
            );
          }
          if (isAddPayIdManual) {
            console.log(
              `⭐ ${PAYTO_LOG_TAG} 👉 isAllowSubmit ➡️ isAddPayIdManual ➡️ paytoAccountInformation ↩`,
              paytoAccountInformation,
            );
          }
          onAddPaytoAccountAuthorise(() => {
            onClose();
            const modalOptions = { isAddManual, isAddPayIdManual };
            onShowAuthorisePaytoAccountModal(modalOptions);
          }, onOpenPaytoErrorCodeModal);
        } else {
          if (isAddPayIdManual) {
            if (
              !paytoAccountInformation?.[FORM_ADD_PAYTO_PAYID_MOBILE_EMAIL_NAME]
            ) {
              if (
                !newPaytoAccountValidation?.[
                  FORM_ADD_PAYTO_PAYID_MOBILE_EMAIL_NAME
                ]
              ) {
                newPaytoAccountValidation = checkFormInputValidation(
                  paytoAccountInformation?.[
                    FORM_ADD_PAYTO_PAYID_MOBILE_EMAIL_NAME
                  ],
                  FORM_ADD_PAYTO_PAYID_MOBILE_EMAIL_FIELD_REFERENCES?.[
                    payIdType
                  ],
                  newPaytoAccountValidation,
                );
              }

              if (
                paytoAccountInformation?.[
                  FORM_ADD_PAYTO_PAYID_MOBILE_EMAIL_NAME
                ]
              ) {
                if (
                  paytoAccountInformation?.[FORM_ADD_PAYTO_PAYID_TYPE_NAME] ===
                  PAYID_TYPE_MOBILE
                ) {
                  newPaytoAccountValidation = {
                    ...newPaytoAccountValidation,
                    [FORM_ADD_PAYTO_PAYID_MOBILE_EMAIL_NAME]:
                      FORM_ADD_PAYTO_PAYID_MOBILE_FIELD?.msgInvalid,
                  };
                }
              }
            }
          }

          if (isAddManual) {
            if (!paytoAccountInformation?.[FORM_ADD_PAYTO_BSB_NAME]) {
              newPaytoAccountValidation = checkFormInputValidation(
                paytoAccountInformation?.[FORM_ADD_PAYTO_BSB_NAME],
                FORM_ADD_PAYTO_BSB_FIELD,
                newPaytoAccountValidation,
              );
            }
            if (
              !paytoAccountInformation?.[FORM_ADD_PAYTO_ACCOUNT_NUMBER_NAME]
            ) {
              if (!paytoAccountValidation?.[FORM_ADD_PAYTO_BSB_NAME]) {
                newPaytoAccountValidation = checkFormInputValidation(
                  paytoAccountInformation?.[FORM_ADD_PAYTO_ACCOUNT_NUMBER_NAME],
                  FORM_ADD_PAYTO_ACCOUNT_NUMBER_FIELD,
                  newPaytoAccountValidation,
                );
              }
            }
          }

          setPaytoAccountValidation(newPaytoAccountValidation);
        }
      } catch (error) {
        console.error(
          `🔥 ${PAYTO_LOG_TAG} ❌ handleOnSubmit ➡️ ERROR ↩`,
          error?.message,
        );
      }
    };

    const handleOnClose = () => {
      if (!isEmpty(paytoAccountValidation?.[FORM_ADD_PAYTO_MAX_AMOUNT_NAME])) {
        resetPaytoAccountInformation();
      } else {
        resetPaytoAccountInformation({
          [FORM_ADD_PAYTO_MAX_AMOUNT_NAME]: Number(maxAmount),
        });
      }
      if (!isEmpty(paytoAccountsAgreement)) {
        onSelectPaytoAccountAgreement();
      }
      onClose();
    };

    const renderAccountNumberVerified = () => {
      if (!isArray(paytoBankAccountsVerified)) return null;
      return (
        <>
          <SectionDesc>{t('label_add_payto_account_popup_desc_1')}</SectionDesc>
          <SizeBox height={4} />
          {paytoBankAccountsVerified.map((d, i) => (
            <Fragment key={i}>
              <BankAccountVerifiedItem
                data={d}
                paytoAccountInformation={paytoAccountInformation}
                setPaytoAccountInformation={setPaytoAccountInformation}
                paytoAccountValidation={paytoAccountValidation}
                setPaytoAccountValidation={setPaytoAccountValidation}
                paytoAccountInformationRetry={paytoAccountInformationRetry}
                setAddManual={setAddManual}
                setAddPayIdManual={setAddPayIdManual}
              />
              <SizeBox height={10} />
            </Fragment>
          ))}
        </>
      );
    };
    const renderUseOtherPaytoAccount = () => {
      return (
        <>
          <OtherAccountNumberVerifiedItem
            data={{ senderBankId: 'other' }}
            paytoAccountInformation={paytoAccountInformation}
            setPaytoAccountInformation={setPaytoAccountInformation}
            paytoAccountValidation={paytoAccountValidation}
            setPaytoAccountValidation={setPaytoAccountValidation}
            paytoAccountInformationRetry={paytoAccountInformationRetry}
            setAddManual={setAddManual}
            setAddPayIdManual={setAddPayIdManual}
          />
        </>
      );
    };

    const { FormLabel } = useFormInput(
      FORM_ADD_PAYTO_LIMIT_PER_AGREEMENT_FIELD,
    );
    const renderLimitPerAgreementField = () => {
      return (
        <>
          <FormLabel />
          <SectionDesc style={fontSize16MixinObject}>
            {t('label_payto_limit_per_agreement_desc')}
          </SectionDesc>
          <SizeBox height={16} />
        </>
      );
    };

    const renderPaytoMaxAmountField = () => {
      // DEV-1401: hide the maxAmount UI entirely while the applicable
      // validation is unresolved (row #5) -- never gated on the shared
      // isLoading flag.
      if (isCreateLimitCheckPending) return null;

      const filteredOptions = getMaxAmountOptionsForCreateLimit(
        MAX_AMOUNT_OPTION_DATA,
      );
      const isSingleOptionRemaining = filteredOptions.length === 1;
      const effectiveIsDisabled = isSingleOptionRemaining;
      const selectedValue = isSingleOptionRemaining
        ? filteredOptions[0].value
        : `${paytoAccountInformation[FORM_ADD_PAYTO_MAX_AMOUNT_NAME]}`;

      return (
        <BodyContentSection>
          <Label>{t(maxAmountFieldLabel)}</Label>
          <TooltipCommon
            field={FORM_ADD_PAYTO_MAX_AMOUNT_FIELD}
            text={`${t('label_max_amount_tooltip')}`}
            buttonStyles={{
              marginLeft: '0',
            }}
            isUseImage
          />
          <SectionDesc style={fontSize16MixinObject}>
            <FormSelectionMaxAmountValidation
              field={FORM_ADD_PAYTO_MAX_AMOUNT_FIELD}
              data={filteredOptions}
              keyFind="key"
              keyProp="value"
              dropdownItemPrefix="AUD"
              information={{
                ...paytoAccountInformation,
                [FORM_ADD_PAYTO_MAX_AMOUNT_NAME]: selectedValue,
              }}
              setInformation={setPaytoAccountInformation}
              validation={paytoAccountValidation}
              setValidation={setPaytoAccountValidation}
              isSearchHidden
              scrollUp
              styles={{ marginBottom: 0 }}
              dropdownStyles={{ height: '44px' }}
              dropdownMenuContainerStyles={{
                transform: 'translate(0px, 44px) !important',
              }}
              arrowDownIconColor="var(--ds-c-blue-hyperlink-default)"
              isDisabled={effectiveIsDisabled}
              shouldShowLimitGuidance={shouldShowLimitGuidance}
              isCreateFlowMaxAmountCheck
            />
          </SectionDesc>
        </BodyContentSection>
      );
    };
    // DEV-1401: gates on confirmedBsb (set only by a successful,
    // PayTo-supporting BSB check, cleared on every edit/empty/partial/error
    // path -- see withCheckBSBNumber.jsx) matched against the *current*
    // BSB field value, mirroring renderPaytoMaxAmountFieldByPayId's pattern
    // below. Replaces a prior bankCode-truthiness check that stayed true
    // after the user edited away from an already-confirmed BSB (bankCode is
    // never cleared by the empty/partial branches, since other consumers --
    // usePaytoAPI.jsx, GuidelineIframeModal, withAddPaytoBankVerified.jsx --
    // still need it regardless of PayTo support) and that didn't account for
    // supportsPayTo === false, both of which could show a stale/incorrect
    // maxAmount dropdown.
    const renderPaytoMaxAmountFieldByBSB = () => {
      const currentBsb = stringUtils.replaceAllCharacter(
        paytoAccountInformation?.[FORM_ADD_PAYTO_BSB_NAME],
      );
      const { confirmedBsb } = paytoAccountInformation || {};
      if (!confirmedBsb || confirmedBsb !== currentBsb) return null;
      return renderPaytoMaxAmountField();
    };

    // DEV-1401: PayID-parity gate mirroring renderPaytoMaxAmountFieldByBSB
    // above. Unlike BSB's existence-only check (bankCode truthy), this
    // requires the current PayID value/type to still equal the exact
    // value/type a successful validatePayId response confirmed
    // (confirmedPayId/confirmedPayIdType, set in withCheckMobileNumber.jsx)
    // -- necessary because paytoPayIdCheckPending alone doesn't cover the
    // pre-debounce phone-format-check phase, and a same-type edit doesn't
    // otherwise invalidate a stale confirmed value the way BSB's bankCode
    // gets wiped by a full-object replace.
    const renderPaytoMaxAmountFieldByPayId = () => {
      const currentPayId =
        paytoAccountInformation?.[FORM_ADD_PAYTO_PAYID_MOBILE_EMAIL_NAME];
      const { confirmedPayId, confirmedPayIdType } =
        paytoAccountInformation || {};
      if (
        !confirmedPayId ||
        confirmedPayId !== currentPayId ||
        confirmedPayIdType !== payIdType
      )
        return null;
      return renderPaytoMaxAmountField();
    };

    const renderPaytoBankAccountsVerified = () => {
      return (
        <>
          <SizeBox height={20} />
          <BodyContentSection>
            {/* {renderAccountNumberVerified()} */}
            {renderUseOtherPaytoAccount()}
          </BodyContentSection>
          {isAddManual && (
            <>
              <SizeBox height={20} />
              <FormAddPaytoAccountManual
                paytoAccountInformation={paytoAccountInformation}
                setPaytoAccountInformation={setPaytoAccountInformation}
                paytoAccountValidation={paytoAccountValidation}
                setPaytoAccountValidation={setPaytoAccountValidation}
                paytoAccountInformationRetry={paytoAccountInformationRetry}
                onPaytoSystemError={onPaytoSystemError}
              />
              {renderLimitPerAgreementField()}
              {renderPaytoMaxAmountFieldByBSB()}
            </>
          )}
        </>
      );
    };

    const renderUsePayIdPaytoAccount = () => {
      return (
        <>
          <SectionDesc>{t('label_or')}:</SectionDesc>
          <SizeBox height={4} />
          <PayIdAccountNumberVerifiedItem
            data={{ senderBankId: 'payid' }}
            paytoAccountInformation={paytoAccountInformation}
            setPaytoAccountInformation={setPaytoAccountInformation}
            paytoAccountValidation={paytoAccountValidation}
            setPaytoAccountValidation={setPaytoAccountValidation}
            paytoAccountInformationRetry={paytoAccountInformationRetry}
            setAddManual={setAddManual}
            setAddPayIdManual={setAddPayIdManual}
          />
        </>
      );
    };
    const renderPaytoPayIdKnownFailureRow = () => {
      if (!shouldShowPayIdBankValidationFailure) return null;
      return (
        <PayIdBankValidationFailureRow>
          {t(paytoAccountValidation?.[FORM_ADD_PAYTO_PAYID_MOBILE_EMAIL_NAME])}
        </PayIdBankValidationFailureRow>
      );
    };
    const renderPaytoPayIdAccountsVerified = () => {
      return (
        <div>
          <SizeBox height={20} />
          <BodyContentSection>
            {renderUsePayIdPaytoAccount()}
          </BodyContentSection>
          {isAddPayIdManual && (
            <>
              <SizeBox height={20} />
              <FormAddPaytoPayIdAccountManual
                paytoAccountInformation={paytoAccountInformation}
                setPaytoAccountInformation={setPaytoAccountInformation}
                paytoAccountValidation={paytoAccountValidation}
                setPaytoAccountValidation={setPaytoAccountValidation}
                paytoAccountInformationRetry={paytoAccountInformationRetry}
                setAddManual={setAddManual}
                setAddPayIdManual={setAddPayIdManual}
                onPaytoSystemError={onPaytoSystemError}
                suppressPayIdMessage={shouldShowPayIdBankValidationFailure}
              />
              {renderPaytoPayIdKnownFailureRow()}
              {renderLimitPerAgreementField()}
              {renderPaytoMaxAmountFieldByPayId()}
            </>
          )}
        </div>
      );
    };

    const renderActionButtons = () => {
      if (!isDesktop)
        return (
          <>
            <ButtonCommon
              value={buttonLabel2}
              color={
                !isAddTypeSelected
                  ? 'var(--ds-c-blue-disabled)'
                  : 'var(--c-primary)'
              }
              background={
                !isAddTypeSelected ? 'var(--ds-bg-4)' : 'var(--bg-primary)'
              }
              isFill={true}
              styles={{
                margin: '0px',
                width: '100%',
              }}
              onClick={handleOnSubmit}
              isUseKeyDown
              isDisabled={!isAddTypeSelected}
            />
          </>
        );
      return (
        <>
          <ButtonCommon
            value={buttonLabel1}
            onClick={handleOnClose}
            styles={{
              margin: '0px',
              width: '50%',
            }}
            color="var(--ds-c-blue)"
            background="var(--ds-c-white)"
          />
          <ButtonCommon
            value={buttonLabel2}
            color={
              !isAddTypeSelected
                ? 'var(--ds-c-blue-disabled)'
                : 'var(--c-primary)'
            }
            background={
              !isAddTypeSelected ? 'var(--ds-bg-4)' : 'var(--bg-primary)'
            }
            isFill={true}
            styles={{
              margin: '0px',
              width: '50%',
            }}
            onClick={handleOnSubmit}
            isUseKeyDown
            isDisabled={!isAddTypeSelected}
          />
        </>
      );
    };

    return (
      <>
        <style>
          {zIndexModal &&
            `
          @media screen and (max-width: ${TABLET_MAX_WIDTH - 1}px) {
            .modal {
              z-index: ${zIndexModal || '1101'};
            }
          }
        `}
          {isBackdropOverlap
            ? `
          .add-payto-account-backdrop {
            z-index: ${zIndexBackdropOverlap || '1100'};
            background: #212121;
            opacity: 0.7!important;
            display: initial!important;
          }
        `
            : `
        .add-payto-account-backdrop {
          background: #212121;
          opacity: 0.7!important;
          display: initial!important;
        }
      `}
        </style>

        <ModalStyled
          show={isOpen}
          onHide={() => {}}
          aria-labelledby="contained-modal-title-vcenter"
          backdropClassName="add-payto-account-backdrop"
          centered
          scrollable={!isDesktop}
          // DEV-1401: disable this modal's own focus-enforcement/Escape
          // handling only while the system-error overlay is shown, so that
          // overlay (rendered outside this Modal's own dialog subtree) can
          // hold focus and Escape without a double-fire back into this
          // modal. Default (overlay hidden) behavior is unchanged.
          enforceFocus={!paytoSystemError.isShow}
          keyboard={!paytoSystemError.isShow}
        >
          <ModalHeader>
            <CloseButton
              src={CloseIcon}
              height={30}
              width={30}
              onClick={handleOnClose}
            />
          </ModalHeader>
          <ModalBody>
            <BodyContentTop>
              <img src={PaytoIcon} height={40} />
              <SizeBox height={20} />
              <Desc>{t('label_add_payto_account_popup_desc')}</Desc>
              <SizeBox height={4} />
              <Desc
                style={{
                  ...paragraphMixinObject,
                  ...fontSize16MixinObject,
                  fontWeight: 500,
                  textAlign: 'center',
                }}
                dangerouslySetInnerHTML={{
                  __html: isHHMTTheme
                    ? t('label_payto_account_popup_desc_hhmt')
                    : t('label_payto_account_popup_desc_remox'),
                }}
              />
            </BodyContentTop>
            {renderPaytoBankAccountsVerified()}
            {renderPaytoPayIdAccountsVerified()}
            <SizeBox height={34} />
            <BodyContentSection>
              <SectionDesc
                style={
                  isDesktop ? fontSize14MixinObject : fontSize12MixinObject
                }
                dangerouslySetInnerHTML={{
                  __html: t('label_add_payto_account_popup_note'),
                }}
              />
            </BodyContentSection>
          </ModalBody>
          <ModalFooter>{renderActionButtons()}</ModalFooter>
        </ModalStyled>

        <PaytoSystemErrorOverlay
          isShow={paytoSystemError.isShow}
          title={paytoSystemError.title}
          content={paytoSystemError.content}
          onClose={onClosePaytoSystemError}
        />
      </>
    );
  },
);

const ModalStyled = styled(Modal)`
  @media screen and (min-width: ${MOBILE_MIN_WIDTH}px) and (max-width: ${TABLET_MAX_WIDTH -
    1}px) {
    & .modal-dialog {
      position: absolute;
      margin: 0;
      top: 0px;
      height: 100dvh;
      width: 100%;
      max-width: 100%;
      align-items: end;
      transition: ease-out 0.3s !important;
      transform: none;

      & .modal-content {
        padding: 0;
        box-shadow: none;
        background: transparent;
        width: 100%;
        height: fit-content;
        margin-inline: auto;
        border: none;
        border-radius: 16px 16px 0 0;
        position: relative;
      }
    }
  }

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    & .modal-dialog {
      transform: none;
      & .modal-content {
        padding: 20px;
        border-radius: 20px;
        box-shadow: var(--ds-bs-4);
        background: var(--ds-c-white);
        width: 476px;
        height: fit-content;
        margin-inline: auto;
        border: none;
      }
    }
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-item: start;
  justify-content: end;
  background: transparent !important;
  width: 100%;
  height: 34px;
  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    display: none;
  }
`;
const ModalBody = styled(Modal.Body)`
  text-align: left;
  padding-block: 16px;
  padding-inline: 16px;
  max-height: 458px;
  background: var(--ds-c-white);
  border-radius: 16px 16px 0 0;

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    margin-bottom: 20px;
    padding-block: unset;
    padding-inline: unset;
    max-height: unset;
    border-radius: unset;
  }
`;
const BodyContentTop = styled.div`
  ${displayFlexColCenterCenterMixin};
`;
const BodyContentSection = styled.div``;
const Label = styled.label`
  ${paragraphMixin};
  font-size: 16px;
`;
const Desc = styled.p`
  ${paragraphMixin};
  font-size: 20px;

  & a {
    color: var(--c-primary) !important;
  }
`;
const SectionDesc = styled.p`
  ${paragraphMixin};
  font-size: 14px;
  font-weight: 500;
`;

// DEV-1401: separate-row PayID bank-validation-failure message -- same
// visual language as FormControlFeedback (components/common/FormControlFeedback)
// but statically positioned (not `position: absolute`), since this renders
// as its own block row rather than anchored beneath a single field.
const PayIdBankValidationFailureRow = styled.p`
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 500;
  font-size: 12px;
  line-height: 14px;
  color: var(--ds-c-red);
  margin: 4px 0 0;
  margin-top: -24px;
  margin-bottom: 16px;

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    font-size: 14px;
    line-height: 17px;
  }
`;

const ModalFooter = styled(Modal.Footer)`
  display: flex;
  justify-content: space-between;
  margin-inline: 0px;
  padding-inline: 16px;
  padding-block: 16px;
  border-top: 1px solid var(--ds-c-grey-disabled);
  background: var(--ds-c-white);
  border-radius: 0;

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    padding-block: unset;
    padding-inline: unset;
    margin-inline: unset;
    border-top: none;
    justify-content: end;
  }
`;

const CloseButton = styled.img`
  width: 30px;
  height: 30px;
  ${cursorPointerMixin};
`;

export default Popup;
